import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useAuthQuery, useAuthPostQuery, useAuthMutation, useAuthHeaders } from "./apiClient";
import axios from "axios";
import endpoints from './endpoints.json';

const TIMEOUT = Number.parseInt(process.env.REACT_APP_TIMEOUT_MS || '10000');
const BASE_URL = process.env.REACT_APP_SERVER_URL;

// Types
export interface NewApplicationValues {
  id?: number | string | null;
  deviceSerial: string | null;
  orgName: string | null;
  region: number | null;
  district: number | null;
  street: number | null;
  addressAdditional: string | null;
  phone: string | null;
  email: string | null;
  activityType: number | null;
  objectType: number | null;
  samModule: string | null;
  simNumber: string | null;
  taxNumber: string | null;
  deviceModel: number | null;
  sealNumber: string | null;
  fiscalNumber: string | null;
}

// Field names are the query parameters PUT /v1/application/{id}/newAddress expects
export interface ChangeAddressFormValues {
  id: number;
  region: string | number;
  district: string | number;
  street: string | number;
  addressAdditional: string | null;
  phone: string | null;
}

export type OptionType = {
  id: number | string;
  name: string;
};

export interface DocumentListFilterFields {
  deviceSerial?: string;
  samModule?: string;
  sealNumber?: string;
  taxNumber?: string;
}

export type UseDocumentListProps = {
  sort: string;
  size: number;
  page: number;
  filter: DocumentListFilterFields;
};

export type UserListFilterProps = {
  user?: string;
};

export type UseUserListQueryProps = {
  keys?: UserListFilterProps;
  sort: string;
  size?: number;
  page?: number;
};

export interface UpdateUserValues {
  username: string;
  enabled: boolean;
}

export interface NewUserValues extends UpdateUserValues {
  password: string;
  organisation: string;
  userType: string;
  email: string;
}

// Query key constants
const QUERY_KEYS = {
  DOCUMENT_LIST: 'GetDocumentList',
  OBJECT_TYPES: 'ObjectTypes',
  DEVICE_MODELS: 'DeviceModels',
  ACTIVITY_TYPES: 'ActivityTypes',
  REGION: 'Region',
  STATUSES: 'Statuses',
  ORGANIZATIONS: 'Organizations',
  USER_LIST: 'GetUserListQuery',
} as const;

// Utility
export function invalidateDocumentList(queryClient: QueryClient) {
  queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_LIST]);
}

// Document List - POST query (special case)
export function useDocumentList(props: UseDocumentListProps) {
  const url = `${endpoints.DOCUMENT_LIST}?sort=${props.sort}&size=${props.size}&page=${props.page}`;
  return useAuthPostQuery(
    [QUERY_KEYS.DOCUMENT_LIST, props.sort, props.size, props.page, props.filter],
    url,
    props.filter
  );
}

// Dictionaries rarely change (the server caches them for an hour too), so don't refetch on every mount
const DICTIONARY_CACHE_MS = 60 * 60 * 1000;
const DICTIONARY_OPTIONS = { staleTime: DICTIONARY_CACHE_MS, cacheTime: DICTIONARY_CACHE_MS };

// Simple GET queries
export function useObjectTypes() {
  return useAuthQuery([QUERY_KEYS.OBJECT_TYPES], endpoints.OBJECT_TYPES, DICTIONARY_OPTIONS);
}

export function useDeviceModels() {
  return useAuthQuery([QUERY_KEYS.DEVICE_MODELS], endpoints.DEVICE_MODELS, DICTIONARY_OPTIONS);
}

export function useActivityTypes() {
  return useAuthQuery([QUERY_KEYS.ACTIVITY_TYPES], endpoints.ACTIVITY_TYPES, DICTIONARY_OPTIONS);
}

export function useRegion() {
  return useAuthQuery([QUERY_KEYS.REGION], endpoints.REGION, DICTIONARY_OPTIONS);
}

export function useStatusOptions() {
  return useAuthQuery([QUERY_KEYS.STATUSES], endpoints.STATUS, DICTIONARY_OPTIONS);
}

export function useOrganizations() {
  return useAuthQuery([QUERY_KEYS.ORGANIZATIONS], endpoints.ORGANIZATIONS);
}

// Parameterized GET queries
export function useDistrict(regionId: string | number | undefined) {
  return useAuthQuery(
    [`DistrictForRegion${regionId}`],
    `${endpoints.DISTRICT}/${regionId}`,
    { ...DICTIONARY_OPTIONS, enabled: regionId !== undefined && regionId !== '' }
  );
}

export function useStreet(districtId: string | undefined) {
  return useAuthQuery(
    [`StreetForRegion${districtId}`],
    `${endpoints.STREET}/${districtId}`,
    { ...DICTIONARY_OPTIONS, enabled: districtId !== undefined && districtId !== '' }
  );
}

export function useDraftRetriever(draftId: number | string | null) {
  return useAuthQuery(
    [`RetrieveDraft${draftId}`],
    `${endpoints.APPLICATION}/${draftId}`,
    { enabled: draftId !== null && draftId !== undefined }
  );
}

export function useUserRetriever(userName: string | null) {
  return useAuthQuery(
    [`RetrieveUser${userName}`],
    `${endpoints.USERS}/${userName}`,
    { enabled: userName !== null }
  );
}

// Mutations
export function useDeviceSerialChecker() {
  return useAuthMutation<unknown, string>(
    ['DeviceSerialChecker'],
    'GET',
    (taxInfo) => `${endpoints.TAX_INFO}/${taxInfo}`
  );
}

export function useDocumentDeregistrator() {
  return useAuthMutation<unknown, number | null>(
    ['DeregisterDocument'],
    'DELETE',
    (documentId) => `${endpoints.APPLICATION}/${documentId}`
  );
}

export function useDocumentChangeAddress() {
  return useAuthMutation<unknown, ChangeAddressFormValues>(
    ['ChangeAddressDocument'],
    'PUT',
    (data) => `${endpoints.APPLICATION}/${data.id}/newAddress`,
    { getParams: (data) => data }
  );
}

export function useDraftRemover() {
  return useAuthMutation<unknown, number | null>(
    ['DeleteDraft'],
    'DELETE',
    (draftId) => `${endpoints.DRAFT}/${draftId}`,
    { invalidateKeys: [[QUERY_KEYS.DOCUMENT_LIST]] }
  );
}

export function useDraftSaver() {
  const queryClient = useQueryClient();
  const { headers } = useAuthHeaders();

  return {
    ...useAuthMutation<unknown, NewApplicationValues>(
      ['SaveDraft'],
      'POST',
      () => endpoints.DRAFT,
      {
        getData: (data) => data,
        invalidateKeys: [[QUERY_KEYS.DOCUMENT_LIST]],
      }
    ),
    // Override mutate to handle extra invalidation
    mutateAsync: async (formData: NewApplicationValues) => {
      const response = await axios.post(`${BASE_URL}/${endpoints.DRAFT}`, formData, {
        headers,
        timeout: TIMEOUT,
      });
      queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_LIST]);
      queryClient.invalidateQueries([`RetrieveDraft${formData.id}`]);
      return response.data;
    },
  };
}

export function useApplicationSender() {
  return useAuthMutation<unknown, NewApplicationValues>(
    ['SendApplication'],
    'POST',
    () => endpoints.APPLICATION,
    {
      getData: (data) => data,
      invalidateKeys: [[QUERY_KEYS.DOCUMENT_LIST]],
    }
  );
}

export function useUserCreator(modify: boolean = false) {
  return useAuthMutation<unknown, NewUserValues | UpdateUserValues>(
    ['CreateUser'],
    modify ? 'PUT' : 'POST',
    () => endpoints.USERS,
    {
      getData: (data) => data,
      invalidateKeys: [[QUERY_KEYS.USER_LIST]],
    }
  );
}

// User list queries
export function useUserListQuery(props: UseUserListQueryProps) {
  const args: string[] = [];
  if (props.sort) args.push(`sort=${props.sort}`);
  if (props.size) args.push(`size=${props.size}`);
  if (props.page) args.push(`page=${props.page}`);

  const userPath = props.keys?.user ? `/${props.keys.user}` : '';
  const queryString = args.length ? `?${args.join('&')}` : '';
  const endpoint = `${endpoints.USERS}${userPath}${queryString}`;

  return useAuthQuery(
    [QUERY_KEYS.USER_LIST, props.keys?.user, props.sort, props.size, props.page],
    endpoint
  );
}
